import { BaseComponent } from 'src/app/shared/components/base.component';
import { Input, ViewChild, ElementRef, Directive } from '@angular/core';
import { MatAutocomplete, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { fromEvent, Observable } from 'rxjs';
import { takeUntil, debounceTime, map, finalize, startWith, distinctUntilChanged, tap, switchMap, take } from 'rxjs/operators';
import { ControlContainer, FormGroup } from '@angular/forms';
import { AuthenticationService } from 'src/core/http/authentication.service';


@Directive()
export abstract  class BaseInifiteAutocomplete extends BaseComponent {
    @Input() label: string;
    @Input() initSelection: any = null;
    @Input() requiredLabel: string = 'selection required';
    @Input() inputTextDisplayProperty: string = null;
    @Input() inputTextDisplayFunction: (value: any) => string | null;
    @Input() groupDisplayProperty: string = null;
    @Input() groupDisplayFunction: (value: any) => string | null;
    @Input() displayProperty: string = null;
    @Input() displayFunction: (value: any) => string | null;
    @Input() childrenProperty: string = null;
    @Input() source: (currentIndex: number, pageSize: number, query: string) => Observable<any>;
    @Input() pageSize = 30;
    @Input() panelWidth: number | string;
    @Input() openPanelIfNoValueOnInit = false;
    @Input() debounceTime = 200;

    replaceEmptyStringWithNullYN = false;

    filteredItems: any[] = [];
    isLoading = false;
    public allPagesLoadedYN = false;
    protected currentIndex = 0;

    @ViewChild('inputRef') inputRef: ElementRef<HTMLInputElement>;
    @ViewChild(MatAutocomplete) autocompleteRef: MatAutocomplete;
    @ViewChild(MatAutocompleteTrigger) autocompleteTrigger: MatAutocompleteTrigger;


    constructor(
        public controlContainer: ControlContainer,
        protected authenticationService: AuthenticationService,
        ) {
        super(authenticationService);
    }


    monitorInputValueChanges() {
        this.formControls.inputControl.valueChanges
            .pipe(
                takeUntil(this.unsubscribe),
                startWith(null),
                debounceTime(this.debounceTime),
                // distinctUntilChanged(),
                tap((value) => {
                    this.isLoading = true;
                    this.currentIndex = 0;
                    this.allPagesLoadedYN = false;
                    // Set any items previouly bound to an empty list.
                    this.filteredItems = [];
                    
                    if (this.replaceEmptyStringWithNullYN 
                        && typeof value === "string" 
                        && value.length == 0) {    
                        //When a (non-required) field had value and user makes it empty, the value returned by this.formControls.inputControl will  be null (instead of "").
                        this.formControls.inputControl.setValue(null, { emitEvent: false });
                    }
                }),
                switchMap(value => {
                    // if (this.inputRef.nativeElement.value && !this.inputRef.nativeElement.value.startsWith(' ')) {
                    //     this.inputRef.nativeElement.value = ' ' + this.inputRef.nativeElement.value;
                    // }
                    // console.log('VALUE CHANGED', value);
                    // console.log('NATIVE VALUE', this.inputRef.nativeElement.value);

                    // let formattedValue = value;
                    // if (typeof value !== 'string') {
                    //   formattedValue = this.inputInternalDisplayItemFn(value);
                    // }

                    return this.source.call(this, this.currentIndex, this.pageSize, this.inputRef?.nativeElement.value.trim())
                    // return this.source.call(this, this.currentIndex, this.pageSize, formattedValue)
                    .pipe(
                        take(1),
                        // delay(3000),
                        finalize(() => this.isLoading = false),
                    );
                })
            )
        .subscribe((rawResult: any) => {
            this.processLoadedData(rawResult);
        });
    }


    // public forceReloadList() {
    //     this.isLoading = true;
    //     this.currentIndex = 0;
    //     this.allPagesLoadedYN = false;
    //     // Set any items previouly bound to an empty list.
    //     this.filteredItems = [];
    //     this.source.call(this, this.currentIndex, this.pageSize, this.inputRef.nativeElement.value)
    //     .pipe(
    //         take(1),
    //         // delay(2000),
    //         finalize(() => this.isLoading = false),
    //     )
    //     .subscribe((rawResult: any) => {
    //         this.processLoadedData(rawResult);
    //     });
    // }


    get formControls() { return this.controlContainer.control['controls']; }
    get controlContainerControl() { return this.controlContainer.control as FormGroup; }


    inputInternalDisplayItemFn = (item: any) => {
        if (item) {
          if (this.inputTextDisplayFunction) {
            return this.inputTextDisplayFunction(item);
          } else {
            if (this.inputTextDisplayProperty && this.inputTextDisplayProperty.length > 0) {
              return item[this.inputTextDisplayProperty];
            } else {
              return this.internalDisplayItemFn(item);
            }
          }
        }
        return '';
    }


    internalDisplayItemFn = (item: any) => {
        if (item) {
            if (this.displayFunction) {
            return this.displayFunction(item);
            } else {
                if (this.displayProperty && this.displayProperty.length > 0) {
                    return item[this.displayProperty];
                } else {
                    return item;
                }
            }
        }
        return '';
    }


    internalDisplayGroupFn = (group: any) => {
        if (group) {
            if (this.groupDisplayFunction) {
            return this.groupDisplayFunction(group);
            } else {
                if (this.groupDisplayProperty && this.groupDisplayProperty.length > 0) {
                    return group[this.groupDisplayProperty];
                } else {
                    return group;
                }
            }
        }
        return '';
    }


    autocompleteScroll() {
        setTimeout(() => {
            if (
            this.autocompleteRef &&
            this.autocompleteTrigger &&
            this.autocompleteRef.panel
            ) {
            fromEvent(this.autocompleteRef.panel.nativeElement, 'scroll')
                .pipe(
                takeUntil(this.unsubscribe),
                debounceTime(300),
                map(x => {
                    if (this.autocompleteRef.panel) {
                        return this.autocompleteRef.panel.nativeElement.scrollTop;
                    }
                    return null;
                }),
                takeUntil(this.autocompleteTrigger.panelClosingActions)
                )
                .subscribe(x => {
                if (this.autocompleteRef.panel) {
                    const scrollTop = this.autocompleteRef.panel.nativeElement.scrollTop;
                    const scrollHeight = this.autocompleteRef.panel.nativeElement.scrollHeight;
                    const elementHeight = this.autocompleteRef.panel.nativeElement.clientHeight;
                    const atBottom = scrollHeight - 48 <= scrollTop + elementHeight;
                    if (atBottom) {
                    if (!this.isLoading && !this.allPagesLoadedYN) {
                        this.isLoading = true;
                        setTimeout(() => {
                            this.autocompleteRef.panel.nativeElement.scrollTop += 48 * 2;
                        });
                        this.source.call(this, this.currentIndex, this.pageSize, this.inputRef.nativeElement.value)
                        .pipe(
                            // takeUntil(this.unsubscribe),
                            take(1),
                            // delay(2000),
                            finalize(() => this.isLoading = false),
                        )
                        .subscribe((rawResult: any) => {
                            this.processLoadedData(rawResult);
                        });
                    }
                    }
                }
                });
            }
        });
    }


    processLoadedData(rawResult: any) {
        const result = 'Data' in rawResult ? rawResult.Data : rawResult;
        if (result) {
            const itemsToAppend = result.Items;
            if (this.childrenProperty) {
                this.currentIndex += Math.min(this.pageSize, this.getPageNbItems(result.Items));
                if (this.filteredItems && this.filteredItems.length > 0 && itemsToAppend && itemsToAppend.length > 0) {
                    const lastIndex =  this.filteredItems.length - 1;
                    // If the first group of the new page is same as the one loaded previously, we do not repeat the group, we just add the items to the existing group
                    if (this.filteredItems[lastIndex][this.groupDisplayProperty] === itemsToAppend[0][this.groupDisplayProperty]) {
                    this.filteredItems[lastIndex][this.childrenProperty] = [...this.filteredItems[lastIndex][this.childrenProperty], ...itemsToAppend[0][this.childrenProperty]];
                    itemsToAppend.shift(); // remove first group of the new page (as we added it to the correct group just above)
                    }
                }
            } else {
            this.currentIndex += Math.min(this.pageSize, result.Items.length);
            }
            this.filteredItems = [...(this.filteredItems || []), ...itemsToAppend];

            if (this.currentIndex >= result.TotalItems) {
            this.allPagesLoadedYN = true;
            }
        }
    }


    /**
     * @return the sum of the number of children items inside all groups
     */
    getPageNbItems(groupArray: []): number {
        let newArray = [];
        for (const groupItem of groupArray) {
            newArray = newArray.concat((groupItem[this.childrenProperty] || []) );
        }
        return newArray.length;
    }

}
